/**
 * GPU Galaxy Simulation - direct port of dgreenheck/webgpu-galaxy
 *
 * Uses Three.js WebGPU renderer + TSL compute shaders to simulate
 * a spiral galaxy with 100k+ stars, dust clouds, mouse interaction,
 * and spring-back physics — all on the GPU.
 */

import * as THREE from "three/webgpu";
import {
  uniform,
  instancedArray,
  instanceIndex,
  vec3,
  vec4,
  float,
  Fn,
  mix,
  length,
  sin,
  cos,
  step,
  uv,
  smoothstep,
} from "three/tsl";

import {
  hash,
  applyDifferentialRotation,
  applyMouseForce,
  applySpringForce,
} from "./helpers";

export interface GalaxyConfig {
  starCount: number;
  cloudCount: number;
  galaxyRadius: number;
  galaxyThickness: number;
  spiralTightness: number;
  armCount: number;
  armWidth: number;
  randomness: number;
  rotationSpeed: number;
  mouseForce: number;
  mouseRadius: number;
  particleSize: number;
  cloudSize: number;
  cloudOpacity: number;
  starBrightness: number;
  denseStarColor: string;
  sparseStarColor: string;
  cloudTintColor: string;
}

export class GalaxySimulation {
  scene: THREE.Scene;
  config: GalaxyConfig;
  COUNT: number;

  spawnPositionBuffer: any = null;
  originalPositionBuffer: any = null;
  velocityBuffer: any = null;
  densityFactorBuffer: any = null;

  computeInit: any = null;
  computeUpdate: any = null;
  cloudInit: any = null;
  cloudUpdate: any = null;

  galaxy: THREE.Sprite | null = null;
  cloudPlane: THREE.Sprite | null = null;

  uniforms: any;

  initialized = false;
  cloudInitialized = false;
  cloudCount = 0;

  constructor(scene: THREE.Scene, config: GalaxyConfig) {
    this.scene = scene;
    this.config = config;
    this.COUNT = config.starCount;
    this.initializeUniforms(config);
  }

  initializeUniforms(config: GalaxyConfig) {
    this.uniforms = {
      compute: {
        time: uniform(0),
        deltaTime: uniform(0.016),
        mouse: uniform(new THREE.Vector3(0, 0, 0)),
        mouseActive: uniform(0.0),
        mouseForce: uniform(config.mouseForce),
        mouseRadius: uniform(config.mouseRadius),
        rotationSpeed: uniform(config.rotationSpeed),
      },
      galaxy: {
        radius: uniform(config.galaxyRadius),
        thickness: uniform(config.galaxyThickness),
        spiralTightness: uniform(config.spiralTightness),
        armCount: uniform(config.armCount),
        armWidth: uniform(config.armWidth),
        randomness: uniform(config.randomness),
      },
      visual: {
        particleSize: uniform(config.particleSize),
        cloudSize: uniform(config.cloudSize),
        cloudOpacity: uniform(config.cloudOpacity),
        starBrightness: uniform(config.starBrightness),
        denseStarColor: uniform(new THREE.Color(config.denseStarColor)),
        sparseStarColor: uniform(new THREE.Color(config.sparseStarColor)),
        cloudTintColor: uniform(new THREE.Color(config.cloudTintColor)),
      },
    };
  }

  createGalaxySystem() {
    if (this.galaxy) {
      this.scene.remove(this.galaxy);
      (this.galaxy.material as any)?.dispose?.();
    }

    this.spawnPositionBuffer = instancedArray(this.COUNT, "vec3");
    this.originalPositionBuffer = instancedArray(this.COUNT, "vec3");
    this.velocityBuffer = instancedArray(this.COUNT, "vec3");
    this.densityFactorBuffer = instancedArray(this.COUNT, "float");

    const U = this.uniforms;

    this.computeInit = Fn(() => {
      const idx = instanceIndex;
      const seed = idx.toFloat();

      const radius = hash(seed.add(1)).pow(0.5).mul(U.galaxy.radius);
      const normalizedRadius = radius.div(U.galaxy.radius);

      const armIndex = hash(seed.add(2)).mul(U.galaxy.armCount).floor();
      const armAngle = armIndex.mul(6.28318).div(U.galaxy.armCount);

      const spiralAngle = normalizedRadius
        .mul(U.galaxy.spiralTightness)
        .mul(6.28318);

      const angleOffset = hash(seed.add(3)).sub(0.5).mul(U.galaxy.randomness);
      const radiusOffset = hash(seed.add(4)).sub(0.5).mul(U.galaxy.armWidth);

      const angle = armAngle.add(spiralAngle).add(angleOffset);
      const offsetRadius = radius.add(radiusOffset);

      const x = cos(angle).mul(offsetRadius);
      const z = sin(angle).mul(offsetRadius);

      const thicknessFactor = float(1.0).sub(normalizedRadius).add(0.2);
      const y = hash(seed.add(5))
        .sub(0.5)
        .mul(U.galaxy.thickness)
        .mul(thicknessFactor);

      const position = vec3(x, y, z);

      this.spawnPositionBuffer.element(idx).assign(position);
      this.originalPositionBuffer.element(idx).assign(position);

      const orbitalSpeed = float(1.0).div(offsetRadius.add(0.5)).mul(5.0);
      const vx = sin(angle).mul(orbitalSpeed).negate();
      const vz = cos(angle).mul(orbitalSpeed);
      this.velocityBuffer.element(idx).assign(vec3(vx, float(0), vz));

      const radialSparsity = radiusOffset
        .abs()
        .div(U.galaxy.armWidth.mul(0.5).add(0.01));
      const angularSparsity = angleOffset
        .abs()
        .div(U.galaxy.randomness.mul(0.5).add(0.01));
      const sparsityFactor = radialSparsity.add(angularSparsity).mul(0.5).min(1.0);

      this.densityFactorBuffer.element(idx).assign(sparsityFactor);
    })().compute(this.COUNT);

    this.computeUpdate = Fn(() => {
      const idx = instanceIndex;
      const position = this.spawnPositionBuffer.element(idx).toVar();
      const originalPos = this.originalPositionBuffer.element(idx);

      const rotatedPos = applyDifferentialRotation(
        position,
        U.compute.rotationSpeed,
        U.compute.deltaTime,
      );
      position.assign(rotatedPos);

      const rotatedOriginal = applyDifferentialRotation(
        originalPos,
        U.compute.rotationSpeed,
        U.compute.deltaTime,
      );
      this.originalPositionBuffer.element(idx).assign(rotatedOriginal);

      const mForce = applyMouseForce(
        position,
        U.compute.mouse,
        U.compute.mouseActive,
        U.compute.mouseForce,
        U.compute.mouseRadius,
        U.compute.deltaTime,
      );
      position.addAssign(mForce);

      const sForce = applySpringForce(
        position,
        rotatedOriginal,
        float(2.0),
        U.compute.deltaTime,
      );
      position.addAssign(sForce);

      this.spawnPositionBuffer.element(idx).assign(position);
    })().compute(this.COUNT);

    const spriteMaterial = new THREE.SpriteNodeMaterial();
    spriteMaterial.transparent = true;
    spriteMaterial.depthWrite = false;
    spriteMaterial.blending = THREE.AdditiveBlending;

    const starPos = this.spawnPositionBuffer.toAttribute();
    const densityFactor = this.densityFactorBuffer.toAttribute();

    const circleShape = Fn(() => {
      const center = uv().sub(0.5).mul(2.0);
      const dist = length(center);
      const alpha = smoothstep(1.0, 0.0, dist).mul(
        smoothstep(1.0, 0.3, dist),
      );
      return alpha;
    })();

    // ~30% of stars are pure white; the rest blend between the dense/sparse colors
    const starSeed = hash(instanceIndex.toFloat().add(99));
    const whiteMask = step(starSeed, float(0.3));
    const tintedColor = mix(
      vec3(U.visual.denseStarColor),
      vec3(U.visual.sparseStarColor),
      densityFactor,
    );
    const starColorNode = mix(tintedColor, vec3(1.0, 1.0, 1.0), whiteMask).mul(
      U.visual.starBrightness,
    );

    (spriteMaterial as any).positionNode = starPos;
    (spriteMaterial as any).colorNode = vec4(
      starColorNode.x,
      starColorNode.y,
      starColorNode.z,
      float(1.0),
    );
    (spriteMaterial as any).opacityNode = circleShape;
    (spriteMaterial as any).scaleNode = U.visual.particleSize;

    this.galaxy = new THREE.Sprite(spriteMaterial as any);
    (this.galaxy as any).count = this.COUNT;
    this.galaxy.frustumCulled = false;
    this.scene.add(this.galaxy);
  }

  createClouds() {
    if (this.cloudPlane) {
      this.scene.remove(this.cloudPlane);
      (this.cloudPlane.material as any)?.dispose?.();
    }

    const CLOUD_COUNT = this.config.cloudCount;
    const U = this.uniforms;

    const cloudPositionBuffer = instancedArray(CLOUD_COUNT, "vec3");
    const cloudOriginalPositionBuffer = instancedArray(CLOUD_COUNT, "vec3");
    const cloudColorBuffer = instancedArray(CLOUD_COUNT, "vec3");
    const cloudSizeBuffer = instancedArray(CLOUD_COUNT, "float");
    const cloudRotationBuffer = instancedArray(CLOUD_COUNT, "float");

    this.cloudInit = Fn(() => {
      const idx = instanceIndex;
      const seed = idx.toFloat().add(10000);

      const radius = hash(seed.add(1)).pow(0.7).mul(U.galaxy.radius);
      const normalizedRadius = radius.div(U.galaxy.radius);

      const armIndex = hash(seed.add(2)).mul(U.galaxy.armCount).floor();
      const armAngle = armIndex.mul(6.28318).div(U.galaxy.armCount);

      const spiralAngle = normalizedRadius
        .mul(U.galaxy.spiralTightness)
        .mul(6.28318);

      const angleOffset = hash(seed.add(3)).sub(0.5).mul(U.galaxy.randomness);
      const radiusOffset = hash(seed.add(4)).sub(0.5).mul(U.galaxy.armWidth);

      const angle = armAngle.add(spiralAngle).add(angleOffset);
      const offsetRadius = radius.add(radiusOffset);

      const x = cos(angle).mul(offsetRadius);
      const z = sin(angle).mul(offsetRadius);

      const thicknessFactor = float(1.0).sub(normalizedRadius).add(0.15);
      const y = hash(seed.add(5))
        .sub(0.5)
        .mul(U.galaxy.thickness)
        .mul(thicknessFactor);

      const position = vec3(x, y, z);

      cloudPositionBuffer.element(idx).assign(position);
      cloudOriginalPositionBuffer.element(idx).assign(position);

      const tintColor = vec3(U.visual.cloudTintColor);
      const cloudColor = tintColor.mul(
        float(1.0).sub(normalizedRadius.mul(0.3)),
      );
      cloudColorBuffer.element(idx).assign(cloudColor);

      const densityFactor = float(1.0).sub(normalizedRadius.mul(0.5));
      const size = hash(seed.add(6)).mul(0.5).add(0.7).mul(densityFactor);
      cloudSizeBuffer.element(idx).assign(size);

      const rotation = hash(seed.add(7)).mul(6.28318);
      cloudRotationBuffer.element(idx).assign(rotation);
    })().compute(CLOUD_COUNT);

    this.cloudUpdate = Fn(() => {
      const idx = instanceIndex;
      const position = cloudPositionBuffer.element(idx).toVar();
      const originalPos = cloudOriginalPositionBuffer.element(idx);

      const rotatedPos = applyDifferentialRotation(
        position,
        U.compute.rotationSpeed,
        U.compute.deltaTime,
      );
      position.assign(rotatedPos);

      const rotatedOriginal = applyDifferentialRotation(
        originalPos,
        U.compute.rotationSpeed,
        U.compute.deltaTime,
      );
      cloudOriginalPositionBuffer.element(idx).assign(rotatedOriginal);

      const mForce = applyMouseForce(
        position,
        U.compute.mouse,
        U.compute.mouseActive,
        U.compute.mouseForce,
        U.compute.mouseRadius,
        U.compute.deltaTime,
      );
      position.addAssign(mForce);

      const sForce = applySpringForce(
        position,
        rotatedOriginal,
        float(1.0),
        U.compute.deltaTime,
      );
      position.addAssign(sForce);

      cloudPositionBuffer.element(idx).assign(position);
    })().compute(CLOUD_COUNT);

    this.cloudCount = CLOUD_COUNT;

    const cloudMaterial = new THREE.SpriteNodeMaterial();
    cloudMaterial.transparent = true;
    cloudMaterial.depthWrite = false;
    cloudMaterial.blending = THREE.AdditiveBlending;

    const cloudPos = cloudPositionBuffer.toAttribute();
    const cloudColor = cloudColorBuffer.toAttribute();
    const cloudSize = cloudSizeBuffer.toAttribute();
    const cloudRotation = cloudRotationBuffer.toAttribute();

    // Soft circular cloud shape (procedural, no texture dependency)
    const cloudShape = Fn(() => {
      const center = uv().sub(0.5).mul(2.0);
      const dist = length(center);
      const a = smoothstep(1.0, 0.1, dist);
      return a.pow(2.0);
    })();

    (cloudMaterial as any).positionNode = cloudPos;
    (cloudMaterial as any).colorNode = vec4(
      cloudColor.x,
      cloudColor.y,
      cloudColor.z,
      float(1.0),
    );
    (cloudMaterial as any).scaleNode = cloudSize.mul(U.visual.cloudSize);
    (cloudMaterial as any).rotationNode = cloudRotation;
    (cloudMaterial as any).opacityNode = cloudShape.mul(U.visual.cloudOpacity);

    this.cloudPlane = new THREE.Sprite(cloudMaterial as any);
    (this.cloudPlane as any).count = CLOUD_COUNT;
    this.cloudPlane.frustumCulled = false;
    this.cloudPlane.renderOrder = -1;

    this.scene.add(this.cloudPlane);
    this.cloudInitialized = false;
  }

  async update(
    renderer: any,
    deltaTime: number,
    mouse3D: THREE.Vector3,
    mousePressed: boolean,
  ) {
    if (!this.initialized) {
      await renderer.computeAsync(this.computeInit);
      this.initialized = true;
    }
    if (!this.cloudInitialized && this.cloudInit) {
      await renderer.computeAsync(this.cloudInit);
      this.cloudInitialized = true;
    }

    this.uniforms.compute.time.value += deltaTime;
    this.uniforms.compute.deltaTime.value = deltaTime;
    this.uniforms.compute.mouse.value.copy(mouse3D);
    this.uniforms.compute.mouseActive.value = mousePressed ? 1.0 : 0.0;

    await renderer.computeAsync(this.computeUpdate);
    if (this.cloudUpdate) {
      await renderer.computeAsync(this.cloudUpdate);
    }
  }
}